import { ESubscriptionTier } from '__generated__/globalTypes';
import { UpgradeOffer } from 'components/site/modals/ModalWrapper/ModalContent/contents/UpgradeModal/UpgradeOffer';
import { useContext, useState } from 'react';
import { GlobalContext } from 'session/state/context';
import {
    apiSubscriptionCreatePortalLink,
    apiSubscriptionCreateSubscribeLink
} from 'static/api/directApi';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const UpgradeModal = (props: Props): JSX.Element => {
    const {
        state: { loggedUser }
    } = useContext(GlobalContext);

    const [loading, setLoading] = useState<'creator' | 'pro'>(null);

    async function redirectToSubscribe(level: 'creator' | 'pro') {
        const response = await apiSubscriptionCreateSubscribeLink(level);
        const data = response.data as { redirectUrl: string };

        window.location.href = data.redirectUrl;
    }

    async function redirectToPortal() {
        const response = await apiSubscriptionCreatePortalLink();
        const data = response.data as { redirectUrl: string };

        window.location.href = data.redirectUrl;
    }

    function openChat() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).HubSpotConversations?.widget?.open?.();
    }

    const hasCreatorSubscription =
        loggedUser.subscriptionTier === ESubscriptionTier.TIER_V1_LEVEL_1;
    const hasProSubscription = loggedUser.subscriptionTier === ESubscriptionTier.TIER_V1_LEVEL_2;

    return (
        <S_UpgradeModal className={props.className}>
            <S_UpgradeOffer
                iconName="compass-drafting"
                title={Localization.SUB1}
                price="$14"
                priceSmall={`/${Localization.MO}`}
                description={Localization.SUB1_DESC}
                points={[
                    {
                        valid: true,
                        text: Localization.formatString(
                            Localization.VIEWS_PER_MONTH,
                            2500
                        ).toString()
                    },
                    {
                        valid: true,
                        text: Localization.formatString(Localization.MARKERS_PER_MAP, 50).toString()
                    },
                    {
                        valid: true,
                        text: Localization.formatString(Localization.X_MAPS, 5).toString()
                    },
                    {
                        valid: false,
                        text: Localization.DEDICATED_SUPPORT
                    }
                ]}
                buttonText={
                    hasCreatorSubscription
                        ? Localization.MANAGE_ACTION
                        : hasProSubscription
                        ? Localization.DOWNGRADE_ACTION
                        : Localization.UPGRADE_ACTION
                }
                buttonLoading={loading === 'creator'}
                buttonType={hasCreatorSubscription || hasProSubscription ? 'secondary' : 'primary'}
                buttonAction={() => {
                    setLoading('creator');
                    if (hasCreatorSubscription || hasProSubscription) {
                        redirectToPortal();
                    } else {
                        redirectToSubscribe('creator');
                    }
                }}
                selected={hasCreatorSubscription}
            />
            <S_UpgradeOffer
                iconName="rocket-launch"
                title={Localization.SUB2}
                price="$34"
                priceSmall={`/${Localization.MO}`}
                description={Localization.SUB2_DESC}
                points={[
                    {
                        valid: true,
                        text: Localization.formatString(
                            Localization.VIEWS_PER_MONTH,
                            7500
                        ).toString()
                    },
                    {
                        valid: true,
                        text: Localization.UNLIMITED_MARKERS
                    },
                    {
                        valid: true,
                        text: Localization.UNLIMITED_MAPS
                    },
                    {
                        valid: true,
                        text: Localization.DEDICATED_SUPPORT
                    }
                ]}
                buttonText={
                    hasProSubscription ? Localization.MANAGE_ACTION : Localization.UPGRADE_ACTION
                }
                buttonType={hasProSubscription ? 'secondary' : 'primary'}
                buttonLoading={loading === 'pro'}
                buttonAction={() => {
                    setLoading('pro');
                    if (hasCreatorSubscription || hasProSubscription) {
                        redirectToPortal();
                    } else {
                        redirectToSubscribe('pro');
                    }
                }}
                selected={hasProSubscription}
            />
            <S_UpgradeOffer
                iconName="location-dot"
                title={Localization.SUB3}
                price="$99+"
                description={Localization.SUB3_DESC}
                pointsText={
                    <>
                        {Localization.SUB3_TEXT1}
                        <br />
                        {Localization.SUB3_TEXT2}
                    </>
                }
                buttonText={Localization.CONTACT_US}
                buttonAction={openChat}
            />
        </S_UpgradeModal>
    );
};

const S_UpgradeModal = styled.div`
    display: flex;
    padding: 10px;
`;

const S_UpgradeOffer = styled(UpgradeOffer)`
    &:nth-child(-n + 2) {
        margin-right: 10px;
    }
`;
